import { isValidElement, memo, MouseEvent as ReactMouseEvent, ReactNode, useCallback } from 'react'
import cn from 'classnames'

import { IGeneralProps, IRenderChildrenProps } from './types'

import s from './BaseLink.module.less'

interface IProps extends Omit<IGeneralProps, 'children'> {
  children: (props: IRenderChildrenProps) => JSX.Element
  childrenContent: ReactNode
}

export const BaseLink = memo(function BaseLink({
  styleName = 'bodyDefault',
  className,
  color = 'blue',
  underlineStyle = 'solid',
  target,
  hasVisited,
  isStopPropagation,
  isShowTargetIcon = true,
  hover,
  active,
  focus,
  visited,
  disabled,
  noUnderline,
  childrenContent,
  iconLeft: IconLeft,
  iconRight: IconRight,
  onClick,
  children,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  testId,
  ...props
}: IProps) {
  const handleClick = useCallback(
    (event: ReactMouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (isStopPropagation) {
        event.stopPropagation()
      }

      if (onClick) {
        // TODO какая-то проблема в типизации event
        // @ts-ignore
        return onClick(event)
      }
    },
    [isStopPropagation, onClick]
  )

  const isBlank = target === '_blank'

  let iconLeftPrepared: ReactNode

  if (IconLeft) {
    // @ts-ignore
    iconLeftPrepared = isValidElement(IconLeft) ? IconLeft : <IconLeft />
  }

  let iconRightPrepared: ReactNode

  if (IconRight) {
    // @ts-ignore
    iconRightPrepared = isValidElement(IconRight) ? IconRight : <IconRight />
  }

  const content = (
    <>
      {iconLeftPrepared}
      {childrenContent && <span className={s.text}>{childrenContent}</span>}
      {iconRightPrepared}
    </>
  )

  const preparedClassName = cn(s.Link, className, s[styleName], {
    [s[color]]: !!color,
    [s['Link_has-visited']]: hasVisited,
    [s.Link_blank]: isShowTargetIcon && isBlank,
    [s.Link_dashed]: underlineStyle === 'dashed',
    [s.Link_hover]: hover,
    [s.Link_active]: active,
    [s.Link_focus]: focus,
    [s.Link_visited]: visited,
    [s.Link_disabled]: disabled,
    [s.Link_noUnderline]: noUnderline
  })

  const generalProps: IRenderChildrenProps = {
    ...props,
    className: preparedClassName,
    target,
    rel: isBlank ? 'noopener noreferrer' : '',
    content,
    onClick: handleClick
  }

  return children(generalProps)
})
